import { IonContent, IonProgressBar } from '@ionic/react';
import { Player, PlayerEvent } from '@lottiefiles/react-lottie-player';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { UiTypography } from '@/components/ui/Typography';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { useOnboarding } from '@/pages/Onboarding/context';
import { useQueryPersonalizedAis } from '@/pages/Onboarding/hooks/useQueryPersonalizedAis';
import { canLogEvents } from '@/utils/canLogEvents';
import { preloadImage } from '@/utils/preloadImage';

import { LayoutDataType } from '../../types';

import { CreatingProfileStyles } from './CreatingProfile.styles';
import {
  calculatePercentageByFrame,
  LAST_FRAME,
} from './utils/calculatePercenatageByFrame';
import { getLoaderText } from './utils/getLoaderText';

export const ScreenCreatingProfile: React.FC = () => {
  const creatingProfileTimeoutRef = React.useRef<number | null>(null);
  const isCreatingProfileTimeoutExceeded = React.useRef<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const playerRef = React.useRef<any>(null);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const createdUser = form.watch('createdUser');
  const name = form.getValues('name');
  const loneliness = form.getValues('loneliness');

  const [lottie, setLottie] = React.useState({
    seeker: 0,
  });

  const [enableQuery, setEnableQuery] = React.useState(false);

  React.useEffect(() => {
    if (canLogEvents()) {
      const amplitudeMessage =
        loneliness === 'never'
          ? 'Creating Profile_skipped'
          : 'Creating Profile';

      amplitudeModule.logEvent('onboarding_screen', {
        seen: amplitudeMessage,
      });
    }
  }, [loneliness]);

  React.useEffect(() => {
    creatingProfileTimeoutRef.current = window.setTimeout(() => {
      form.setValue('createdUser', true);
      setLottie({ seeker: 235 });
      if (playerRef.current) {
        playerRef.current.setSeeker(235);
      }
      isCreatingProfileTimeoutExceeded.current = true;
    }, 12000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    setTimeout(() => {
      setEnableQuery(true);
    }, 2000);
  });

  useQueryPersonalizedAis({
    enabled: enableQuery,
    form,
    onSuccess: (data) => {
      form.setValue('characters', data.twins);

      data.twins.forEach((twin) => {
        preloadImage(twin.profile_picture_url);
      });
    },
  });

  return (
    <CreatingProfileStyles>
      <OnboardingNavigation />

      <IonContent className="onboarding-creating-profile">
        <OnboardingTitle>
          {name}, feel relaxed, loved and accepted with Dialogue
        </OnboardingTitle>
        <div className="progress">
          <Player
            className="player"
            src="https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/onboarding/profile/settings.json"
            keepLastFrame
            ref={playerRef}
            loop={false}
            autoplay
            onEvent={(event) => {
              if (isCreatingProfileTimeoutExceeded.current) return;

              if (event === PlayerEvent.Frame) {
                if (playerRef.current) {
                  setLottie(playerRef.current.state);
                }
              }

              if (event === PlayerEvent.Complete) {
                form.setValue('createdUser', true);
                if (creatingProfileTimeoutRef.current) {
                  clearTimeout(creatingProfileTimeoutRef.current);
                }
              }
            }}
          />
          <UiTypography variant="title-medium" className="status">
            {getLoaderText(lottie.seeker)}
          </UiTypography>
          <IonProgressBar value={lottie.seeker / LAST_FRAME} />
          <p className="number">
            {calculatePercentageByFrame(lottie.seeker)} %
          </p>
        </div>
      </IonContent>

      <OnboardingFooter disabled={!createdUser} onContinue={wizard.nextStep} />
    </CreatingProfileStyles>
  );
};
